<template>
  <section class="atlas-opt-in-hero" :class="{ 'atlas-opt-in-hero--mini': isMiniView }">
    <div class="atlas-opt-in-hero__gradient-bg"></div>
    <div class="atlas-opt-in-hero__container">
      <div class="atlas-opt-in-hero__content" :class="{ 'atlas-opt-in-hero__content--mini': isMiniView }">
        <div class="atlas-opt-in-hero__text-section">
          <!-- Verbiage section (varies by context) -->
          <template v-if="isMiniView">
            <h2 class="global-h2 standard-page-header__title atlas-opt-in-hero__mini-header">
              <RouterLink :to="{ name: 'AtlasOptInLanding', params: { customerId } }"> Atlas Social</RouterLink>
            </h2>
            <p class="collab-tiles-section__subtitle">
              Keep your social channels active with authentic local content.
              <span v-if="showSnapshotsMessage" style="display: block; margin-top: 10px">Everything you loved about Social Snapshots, now fully automated!</span>
              <span style="display: block"><em style="font-size: 14px">(at no additional cost!)</em></span>
            </p>
          </template>

          <template v-else-if="isHomepage">
            <h1 class="atlas-opt-in-hero__headline">Atlas Social</h1>
            <h2 class="global-h2 standard-page-header__title">Keep your social channels active with authentic local content</h2>
            <p class="atlas-opt-in-hero__subheading">Shrpa turns your destination's stories &amp; visuals into a consistent, on-brand social feed without adding to your team's workload.</p>
          </template>

          <template v-else>
            <h1 class="atlas-opt-in-hero__headline">Keep your social channels active with authentic local content</h1>
            <p class="atlas-opt-in-hero__subheading">
              Shrpa turns your destination's stories &amp; visuals into a consistent, on-brand social feed without adding to your team's workload.
              <br /><em style="font-size: 14px">(at no additional cost!)</em>
            </p>
          </template>

          <!-- CTA buttons section -->
          <div class="atlas-opt-in-hero__buttons">
            <slot name="cta-button">
              <SrpButton :size="isMiniView ? 'normal' : 'big'" color="teal" fill="solid" :isDisabled="hasOptedIn || isProcessing" @click="trialClicked">
                {{ isProcessing ? "Signing up..." : "Try it Today" }}
              </SrpButton>
            </slot>
            <div v-if="hasOptedIn" class="atlas-opt-in-hero__opted-in-message">
              <strong>Signed up - We'll reach out shortly!</strong>
            </div>
          </div>
          <div class="atlas-opt-in-hero__tagline">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="atlas-opt-in-hero__tagline-icon">
              <path d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm4.3 8.3-4.59 4.59a1 1 0 0 1-1.42 0L7.7 12.3a1 1 0 1 1 1.41-1.41l1.18 1.17 3.88-3.88a1 1 0 0 1 1.41 1.42Z" />
            </svg>
            <span>Rights-ready UGC. Simple approvals. Reliable posting.</span>
          </div>
        </div>

        <div class="atlas-opt-in-hero__mockup-section" :class="{ 'atlas-opt-in-hero__mockup-section--mini': isMiniView }">
          <div class="atlas-opt-in-hero__mockup-card">
            <div class="atlas-opt-in-hero__calendar-widget">
              <div class="atlas-opt-in-hero__calendar-header">
                <div class="atlas-opt-in-hero__calendar-title-area">
                  <div class="atlas-opt-in-hero__calendar-icon"></div>
                  <span class="atlas-opt-in-hero__calendar-title">{{ currentMonth }}</span>
                </div>
                <span class="atlas-opt-in-hero__calendar-badge">Auto-scheduled</span>
              </div>
              <div class="atlas-opt-in-hero__calendar-grid">
                <div
                  v-for="i in totalCalendarCells"
                  :key="i"
                  :class="{
                    'atlas-opt-in-hero__calendar-cell': true,
                    'atlas-opt-in-hero__calendar-cell--blank': i <= calendarOffset,
                    'atlas-opt-in-hero__calendar-cell--highlighted': i > calendarOffset && highlightCells.includes(i - calendarOffset),
                  }"
                >
                  <img
                    v-if="i > calendarOffset && getImageForCell(i - calendarOffset)"
                    :src="getImageForCell(i - calendarOffset)"
                    :alt="`Calendar image ${i - calendarOffset}`"
                    class="atlas-opt-in-hero__calendar-cell-image"
                  />
                  <span v-else-if="i > calendarOffset" class="atlas-opt-in-hero__calendar-cell-number">{{ i - calendarOffset }}</span>
                </div>
              </div>
            </div>
            <div class="atlas-opt-in-hero__status-items">
              <div v-for="status in statusItems" :key="status" class="atlas-opt-in-hero__status-item">
                <div class="atlas-opt-in-hero__status-icon"></div>
                <span class="atlas-opt-in-hero__status-text">{{ status }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import FeatureFlagCache from "@logic/FeatureFlagCache";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import SrpButton from "@components/ui/SrpButton.vue";

const props = withDefaults(
  defineProps<{
    isMiniView?: boolean;
    customerId?: string;
    isHomepage?: boolean;
  }>(),
  {
    isMiniView: false,
    customerId: "",
    isHomepage: false,
  }
);

const getNow = (): Date => {
  try {
    const params = new URLSearchParams(window.location.search);
    const monthParam = params.get("month");
    if (monthParam) {
      const month = parseInt(monthParam, 10);
      if (!isNaN(month) && month >= 1 && month <= 12) {
        const now = new Date();
        return new Date(now.getFullYear(), month - 1, 15);
      }
    }
  } catch {
    // Fallback to current date on any error
  }
  return new Date();
};

const currentMonth = computed(() => {
  return getNow().toLocaleString("en-US", { month: "long" });
});

const daysInMonth = computed(() => {
  const now = getNow();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
});

const totalCalendarCells = computed(() => {
  return daysInMonth.value + calendarOffset;
});

const calendarOffset = 1; // Number of blank cells at the start

const showSnapshotsMessage = computed(() => {
  const cutoffDate = new Date("2026-02-01");
  return getNow() <= cutoffDate;
});

// Hardcoding these to prod images
const imageBaseUrl = "https://cdn.shrpa.com/cms/images/expeditions/thumbtiny/";

// Define seasonal image sets (reusable)
const winterImages = [
  "ad-f251c3a7-3363-473f-9ef2-d4b52c7d74f2.jpg",
  "ad-95804a5f-0f87-4f19-aaa2-0ceeb5b24cef.jpg",
  "cbcf8580-2fa0-4890-806c-3467139a3f6e.jpg",
  "ad-e52bcadd-676c-404f-a61a-701a5d389a94.jpg",
  "bcc2a637-13e4-4bac-a21f-526e1e534322.jpg",
  "ad-4ef5982f-a880-4c73-8c54-fa9cfa10a0ad.jpg",
  "ad-d6911029-f324-4325-a458-221be8132147.jpg",
  "ad-d421469d-73b7-4bcd-bc45-e90741879c5e.jpg",
  "ad-53e33cbe-81b8-4f50-928c-38d1e7d845e2.jpg",
];

const springImages = [
  "837d1488-88cc-4e80-9f4b-2337c2aabe7d.jpg",
  "efe946b3-a3af-4b53-95e4-bc7e33e0a741.jpg",
  "b1ce3864-1171-4dfe-9d93-955ade31d3a1.jpg",
  "dacb1011-5543-4f3e-8476-7bb7d2ea0563.jpg",
  "ad-79d63bab-56c5-4106-93d9-661803162338.jpg",
  "ad-c42e3d85-06b5-452b-bf8d-c79590f392b7.jpg",
  "103ce8ce-60f8-4cfe-afee-9f088c68f141.jpg",
  "86c5b786-4d82-4179-a37e-ee509b6c916f.jpg",
  "c9613a82-ddc8-4efc-8e1b-45bd3cc43bf3.jpg",
  "ad-ac837397-ad1a-405a-a8d1-3c62d56a6de2.jpg",
  "ad-64324b4a-4d89-44d4-aecb-f0c78881b492.jpg",
];

const summerImages = [
  "ad-ed2f7f1a-f99f-43b8-9c88-578ce1e12f64.jpg",
  "ad-047e4127-7775-47fe-9b98-78fb45ec0ae1.jpg",
  "ad-eab2e8f1-d6c6-437e-bbec-f882c50673bb.jpg",
  "ad-00120099-12ae-4df8-a033-1c12436ec007.jpg",
  "ad-987de037-26f5-434e-a567-47b8bbb7b2ec.jpg",
  "ad-47961a0d-8f36-433a-956a-7ba4358c9372.jpg",
  "e6fa785a-57b8-4fc4-a3f2-460a3f437da9.jpg",
  "b97ef9e3-7b94-46b5-a172-890d2ce03680.jpg",
  "ad-d4a05ed3-e312-4e67-90cd-b9663fdb000e.jpg",
  "ad-3cfd4a5d-75a6-4655-82ca-1970731f9682.jpg",
];

const fallImages = [
  "ad-e001606e-c319-4954-bd84-7dfa938639cf.jpg",
  "ad-4d9b4129-f70b-4516-9694-7612dc9a8d86.jpg",
  "ad-a93ce518-099a-4d92-a26a-9585ed1e9908.jpg",
  "ad-6ce9cecd-b404-4793-a73e-f46c61a73931.jpg",
  "c6c5006c-9d23-4b18-aea4-2ec263139e11.jpg",
  "e2b125e8-0dd9-4af8-98b5-65fdc93981cf.jpg",
  "ad-84b1f525-f9c2-4def-b982-8f096b383a4b.jpg",
  "ad-e3c9de3f-f8f7-4bfa-b46c-e107fc67abba.jpg",
  "ad-0449ed67-a0c2-4be6-be6c-827f65da8757.jpg",
  "7bd0ef9f-c2ca-487f-be6a-b9d6ea4c49cb.jpg",
  "198e451d-6198-4e32-b699-e041f08d4c27.jpg",
  "4f78081d-0f4d-485c-b18f-e715eab41fad.jpg",
];

const decemberImages = [
  "ad-1cbf2a2c-4e09-4a9d-87fe-4a123042ef6b.jpg",
  "ad-2a01f4ec-f95a-4533-81bd-7ac6ed4147e6.jpg",
  "ad-42f34a6e-1088-4ea6-a6c5-07363545af61.jpg",
  "ad-8f478196-bd1b-49f0-bdc9-85e24078f402.jpg",
  "ad-8d409b03-3092-41a5-b870-f808061d2721.jpg",
  "ad-3df5aef7-ec9e-4409-8d61-52f47a07077b.jpg",
  "ad-8ff8bb5e-bccc-4013-8360-eadba1b05a14.jpg",
  "ad-d0d5463f-ae6a-469e-9506-cebbea79ec32.jpg",
  "3a40aa30-bac0-47ba-abcb-32dc24c760b7.jpg",
];

// Map each month to its images (1 = January, 12 = December)
const imagesByMonth: Record<number, string[]> = {
  1: winterImages, // January
  2: winterImages, // February
  3: springImages, // March
  4: springImages, // April
  5: springImages, // May
  6: summerImages, // June
  7: summerImages, // July
  8: summerImages, // August
  9: fallImages, // September
  10: fallImages, // October
  11: fallImages, // November
  12: decemberImages, // December
};

// Computed property to get current month's images
const calendarImages = computed(() => {
  const month = getNow().getMonth() + 1; // 1-12
  return imagesByMonth[month] ?? winterImages; // fallback to winter if undefined
});

const highlightCells = ref([2, 5, 9, 11, 14, 18, 22, 26, 30]);
const statusItems = ref(["4 posts approved for next week", "12 new community uploads", "Creator Visit scheduled"]);

const getImageForCell = (cellIndex: number) => {
  const highlightIndex = highlightCells.value.indexOf(cellIndex);
  if (highlightIndex !== -1 && highlightIndex < calendarImages.value.length) {
    return imageBaseUrl + calendarImages.value[highlightIndex];
  }
  return null;
};

const hasOptedIn = ref(false);
const isProcessing = ref(false);

onMounted(async () => {
  if (!props.isHomepage) {
    hasOptedIn.value = await FeatureFlagCache.GetFeature("atlas-opt-in");
  }
});

const trialClicked = async () => {
  if (isProcessing.value || hasOptedIn.value) return;

  isProcessing.value = true;

  await ContentCalendarRepo.optInToAtlas(props.customerId);
  await FeatureFlagCache.Refresh(props.customerId);
  hasOptedIn.value = true;
  isProcessing.value = true;
};
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.atlas-opt-in-hero {
  position: relative;
  // overflow: hidden;
  padding: 64px 0;

  @media (max-width: $tablet-max-width) {
    padding: 40px 0;
  }

  &--mini {
    padding: 0;
  }

  &__gradient-bg {
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(to bottom, #f7faf9 0%, transparent 40%);
  }

  &__container {
    max-width: 1152px;
    margin: 0 auto;
    // padding: 0 16px;
  }

  &__content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    align-items: center;

    @media (max-width: $tablet-max-width) {
      grid-template-columns: 1fr;
    }

    &--mini {
      align-items: start;
    }
  }

  &__text-section {
    display: flex;
    flex-direction: column;
    margin: 0 20px;
  }

  &__headline {
    font-size: 48px;
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: #111827;
    margin: 0 0 16px 0;

    @media (max-width: $tablet-max-width) {
      font-size: 36px;
    }
  }

  &__subheading {
    font-size: 18px;
    line-height: 1.6;
    color: #4b5563;
    margin: 0 0 24px 0;

    &--mini {
      font-size: 18px;
      line-height: 1.6;
      color: #4b5563;
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 24px;
  }

  &__opted-in-message {
    font-size: 16px;

    strong {
      font-weight: 700;
    }
  }

  &__tagline {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 14px;
    color: #4b5563;
  }

  &__tagline-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;

    @media (max-width: $mobile-max-width) {
      display: none;
    }
  }

  &__mockup-section {
    position: relative;

    &--mini {
      max-height: 250px;
      overflow: hidden;

      @media (max-width: $tablet-max-width) {
        display: none;
      }

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 60px;
        background: linear-gradient(to bottom, transparent, white);
        pointer-events: none;
      }
    }
  }

  &__mockup-card {
    border-radius: 16px;
    border: 1px solid #e5e7eb;
    background: white;
    padding: 16px;
    margin: 0 12px;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);

    @media (max-width: $tablet-max-width) {
      margin: 0 2px;
      border-radius: 12px;
      padding: 0;
    }
  }

  &__calendar-widget {
    border-radius: 12px;
    background: #f3f4f6;
    padding: 12px;

    @media (max-width: $tablet-max-width) {
      padding: 12px 6px;
    }
  }

  &__calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  &__calendar-title-area {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__calendar-icon {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background: #d1d5db;
  }

  &__calendar-title {
    font-size: 12px;
    color: #6b7280;
  }

  &__calendar-badge {
    font-size: 12px;
    color: #6b7280;
  }

  &__calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    text-align: center;
  }

  &__calendar-cell {
    display: flex;
    height: 56px;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: white;
    padding: 0;
    overflow: hidden;

    &--blank {
      border: none;
      background: transparent;
    }

    &--highlighted {
      box-shadow: 0 0 0 2px #2b7a78;
      padding: 0;
    }
  }

  &__calendar-cell-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
  }

  &__calendar-cell-number {
    font-size: 14px;
    color: #9ca3af;
    font-weight: 500;
  }

  &__status-items {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__status-item {
    display: flex;
    align-items: center;
    gap: 12px;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 12px;
    background: white;
  }

  &__status-icon {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    background: #d1d5db;
    flex-shrink: 0;
  }

  &__status-text {
    font-size: 14px;
    color: #111827;
  }
}
</style>
